/**
 * Keeps a viewer's replay in step with the applicant's screen.
 *
 * rrweb replays DOM mutations on top of one full snapshot, so a single lost
 * batch (refused by the server, a relay hiccup, a chunked batch that never
 * completed) leaves the picture wrong until the next full snapshot. Signs that
 * the replay drifted:
 * - a gap in the batches' `seq` (the recorder numbers every batch it sends);
 * - a batch that couldn't be read;
 * - rrweb warning that a mutation touched a node the replay doesn't have. A
 *   few are normal, so it takes RESYNC_WARNING_THRESHOLD of them within
 *   RESYNC_WARNING_WINDOW_MS, and only once a full snapshot was replayed;
 * - no full snapshot SNAPSHOT_WAIT_MS after the session went live.
 *
 * Any of them asks the applicant for a fresh full snapshot, and any full
 * snapshot clears them all. Requests are at least RESYNC_MIN_INTERVAL_MS
 * apart, and while unanswered they back off up to RESYNC_MAX_INTERVAL_MS, so
 * a replay that keeps breaking can't flood the applicant's device.
 */

export const SNAPSHOT_WAIT_MS = 10_000;
export const RESYNC_MIN_INTERVAL_MS = 10_000;
export const RESYNC_MAX_INTERVAL_MS = 60_000;
const RESYNC_WARNING_THRESHOLD = 3;
export const RESYNC_WARNING_WINDOW_MS = 5_000;

/** rrweb's EventType.FullSnapshot. */
const FULL_SNAPSHOT = 2;

/**
 * rrweb Replayer warnings that mean the replay no longer matches the page
 * (a mutation for a node it doesn't have, or one it couldn't apply).
 */
const DESYNC_WARNING =
  /not found|exception in mutation|could not remove child|unresolved missing node/i;

export const isDesyncWarning = (args: readonly unknown[]) =>
  args.some((arg) => typeof arg === "string" && DESYNC_WARNING.test(arg));

const hasFullSnapshot = (events: readonly unknown[]) =>
  events.some(
    (event) =>
      typeof event === "object" &&
      event !== null &&
      (event as { type?: unknown }).type === FULL_SNAPSHOT
  );

export type ResyncReason = "gap" | "unreadable" | "replay" | "no_snapshot";

interface StreamHealth {
  /** A complete (reassembled) batch, before it is replayed. */
  batch(seq: number, events: readonly unknown[]): void;
  /** A batch couldn't be read and was dropped. */
  lost(): void;
  /** rrweb's replayer warned that the replay doesn't match the page. */
  replayWarning(): void;
  /** The session is live (again): a full snapshot is due. */
  expectSnapshot(): void;
  /** A new stream (new request, re-join): forget the old one and any retry. */
  reset(): void;
}

export const createStreamHealth = ({
  requestSnapshot,
  now = Date.now
}: {
  /** Asks the applicant for a full snapshot; false if it can't be sent now. */
  requestSnapshot: (reason: ResyncReason) => boolean;
  now?: () => number;
}): StreamHealth => {
  let lastSeq: number | null = null;
  let hasSnapshot = false;
  let lastSnapshotAt = Number.NEGATIVE_INFINITY;
  let warnings: number[] = [];
  // Requests sent since the last full snapshot arrived.
  let unanswered = 0;
  let lastRequestAt = Number.NEGATIVE_INFINITY;
  // "expect": waiting for the snapshot a live session sends by itself.
  // "request": a request is queued, or sent and waiting for its answer.
  let timer: ReturnType<typeof setTimeout> | undefined;
  let waitingFor: "expect" | "request" | undefined;

  const clearTimer = () => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    waitingFor = undefined;
  };

  const schedule = (kind: "expect" | "request", fn: () => void, ms: number) => {
    timer = setTimeout(fn, ms);
    waitingFor = kind;
  };

  const backoff = () =>
    Math.min(
      RESYNC_MIN_INTERVAL_MS * 2 ** Math.max(0, unanswered - 1),
      RESYNC_MAX_INTERVAL_MS
    );

  const send = (reason: ResyncReason) => {
    clearTimer();
    if (!requestSnapshot(reason)) return;
    unanswered += 1;
    lastRequestAt = now();
    warnings = [];
    // No snapshot in time: ask again, backing off.
    schedule("request", () => send("no_snapshot"), backoff());
  };

  const flag = (reason: ResyncReason) => {
    // A snapshot was asked for and is on its way, or a request is queued.
    if (waitingFor === "request") return;
    // Waiting for the session's first snapshot: a loss now is likely that
    // snapshot, so don't wait out the rest.
    clearTimer();
    const wait = lastRequestAt + RESYNC_MIN_INTERVAL_MS - now();
    if (wait <= 0) send(reason);
    else schedule("request", () => send(reason), wait);
  };

  return {
    batch(seq, events) {
      const numbered = Number.isFinite(seq);
      const gap = numbered && lastSeq !== null && seq > lastSeq + 1;
      if (numbered) lastSeq = seq;
      if (hasFullSnapshot(events)) {
        hasSnapshot = true;
        lastSnapshotAt = now();
        unanswered = 0;
        warnings = [];
        clearTimer();
        return;
      }
      if (gap) flag("gap");
    },
    lost() {
      flag("unreadable");
    },
    replayWarning() {
      // Before the first full snapshot nothing can apply; one is on its way.
      if (!hasSnapshot || waitingFor === "request") return;
      const at = now();
      warnings = warnings
        .filter((time) => at - time < RESYNC_WARNING_WINDOW_MS)
        .concat(at);
      if (warnings.length >= RESYNC_WARNING_THRESHOLD) flag("replay");
    },
    expectSnapshot() {
      // One just arrived (it can beat the status update that announced it).
      if (timer !== undefined || now() - lastSnapshotAt < SNAPSHOT_WAIT_MS)
        return;
      schedule("expect", () => send("no_snapshot"), SNAPSHOT_WAIT_MS);
    },
    reset() {
      clearTimer();
      lastSeq = null;
      hasSnapshot = false;
      lastSnapshotAt = Number.NEGATIVE_INFINITY;
      warnings = [];
      unanswered = 0;
    }
  };
};
